import type { ContentType, UrlEntity } from '../domain/note'
import type { IconName } from '../components/Icon'
import {
  isAmazonHostname,
  mapProviderName,
  urlContentCategory,
  urlHostname,
  type UrlContentCategory,
} from '../domain/urlCategory'

export type LinkPreviewCategory = UrlContentCategory

export interface LinkPreviewPresentation {
  actionLabel: string
  category: LinkPreviewCategory
  icon: IconName
  label: string
  pluralLabel: string
  provider: string
}

interface CategoryPresentation {
  actionLabel: string
  icon: IconName
  label: string
  pluralLabel: string
}

const PROVIDER_ICONS: ReadonlyArray<readonly [string, IconName]> = [
  ['instagram.com', 'instagram'],
  ['x.com', 'x'],
  ['twitter.com', 'x'],
  ['tiktok.com', 'tiktok'],
  ['facebook.com', 'facebook'],
  ['reddit.com', 'reddit'],
]

// One glyph per kind across the app: a link and an unpacked screenshot of the same kind share it (screenshotCleanup/card.ts KIND_ICON).
const CATEGORY_PRESENTATIONS: Record<LinkPreviewCategory, CategoryPresentation> = {
  movie: { actionLabel: 'View film', icon: 'film', label: 'Film', pluralLabel: 'films' },
  audio: { actionLabel: 'Listen', icon: 'audio', label: 'Audio', pluralLabel: 'listens' },
  video: { actionLabel: 'Watch', icon: 'play', label: 'Video', pluralLabel: 'videos' },
  social: { actionLabel: 'View post', icon: 'post', label: 'Social', pluralLabel: 'posts' },
  book: { actionLabel: 'View book', icon: 'book', label: 'Book', pluralLabel: 'books' },
  event: { actionLabel: 'View event', icon: 'ticket', label: 'Event', pluralLabel: 'events' },
  reference: { actionLabel: 'Explore', icon: 'book', label: 'Reference', pluralLabel: 'references' },
  document: { actionLabel: 'Open doc', icon: 'document', label: 'Document', pluralLabel: 'documents' },
  article: { actionLabel: 'Read', icon: 'article', label: 'Article', pluralLabel: 'articles' },
  product: { actionLabel: 'View item', icon: 'bag', label: 'Product', pluralLabel: 'items' },
  place: { actionLabel: 'Open map', icon: 'pin', label: 'Place', pluralLabel: 'places' },
  code: { actionLabel: 'Open repo', icon: 'code', label: 'Code', pluralLabel: 'repositories' },
  listing: { actionLabel: 'View listing', icon: 'home', label: 'Listing', pluralLabel: 'listings' },
  recipe: { actionLabel: 'View recipe', icon: 'recipe', label: 'Recipe', pluralLabel: 'recipes' },
  job: { actionLabel: 'View role', icon: 'person', label: 'Job', pluralLabel: 'jobs' },
  link: { actionLabel: 'Visit', icon: 'link', label: 'Link', pluralLabel: 'links' },
}

const PROVIDER_NAMES: ReadonlyArray<readonly [string, string]> = [
  ['imdb.com', 'IMDb'],
  ['letterboxd.com', 'Letterboxd'],
  ['rottentomatoes.com', 'Rotten Tomatoes'],
  ['themoviedb.org', 'TMDB'],
  ['spotify.com', 'Spotify'],
  ['music.apple.com', 'Apple Music'],
  ['podcasts.apple.com', 'Apple Podcasts'],
  ['soundcloud.com', 'SoundCloud'],
  ['bandcamp.com', 'Bandcamp'],
  ['youtube.com', 'YouTube'],
  ['youtu.be', 'YouTube'],
  ['vimeo.com', 'Vimeo'],
  ['tiktok.com', 'TikTok'],
  ['twitch.tv', 'Twitch'],
  ['instagram.com', 'Instagram'],
  ['x.com', 'X'],
  ['twitter.com', 'X'],
  ['threads.net', 'Threads'],
  ['bsky.app', 'Bluesky'],
  ['reddit.com', 'Reddit'],
  ['linkedin.com', 'LinkedIn'],
  ['facebook.com', 'Facebook'],
  ['airbnb.com', 'Airbnb'],
  ['goodreads.com', 'Goodreads'],
  ['meetup.com', 'Meetup'],
  ['meetu.ps', 'Meetup'],
  ['wikipedia.org', 'Wikipedia'],
  ['wikimedia.org', 'Wikimedia'],
  ['docs.google.com', 'Google Docs'],
  ['drive.google.com', 'Google Drive'],
  ['notion.site', 'Notion'],
  ['notion.so', 'Notion'],
  ['figma.com', 'Figma'],
  ['canva.com', 'Canva'],
  ['dropbox.com', 'Dropbox'],
  ['github.com', 'GitHub'],
  ['gitlab.com', 'GitLab'],
  ['bitbucket.org', 'Bitbucket'],
  ['ikea.com', 'IKEA'],
  ['openstreetmap.org', 'OpenStreetMap'],
  ['maps.apple.com', 'Apple Maps'],
] as const

function matchesHost(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`)
}

export function linkPreviewCategory(url: UrlEntity, contentType?: ContentType): LinkPreviewCategory {
  return urlContentCategory(url, contentType)
}

export function linkPreviewProvider(url: UrlEntity): string {
  const mapProvider = mapProviderName(url)
  if (mapProvider) return mapProvider
  const hostname = urlHostname(url)
  if (isAmazonHostname(hostname)) return 'Amazon'
  const knownProvider = PROVIDER_NAMES.find(([domain]) => matchesHost(hostname, domain))?.[1]
  return knownProvider ?? url.siteName?.trim() ?? (hostname || 'Link')
}

export function linkPreviewPresentation(
  url: UrlEntity,
  contentType?: ContentType,
  /** What the link is known to be from beyond its page, such as the note's unpacked screenshot. */
  known?: LinkPreviewCategory,
): LinkPreviewPresentation {
  const category = known ?? linkPreviewCategory(url, contentType)
  const hostname = urlHostname(url)
  const providerIcon = PROVIDER_ICONS.find(([domain]) => matchesHost(hostname, domain))?.[1]
  return {
    category,
    provider: linkPreviewProvider(url),
    ...CATEGORY_PRESENTATIONS[category],
    // Social remains a searchable content type. Recognizable destinations earn a
    // small provider mark; less-specific social sites fall back to the neutral link.
    icon: providerIcon ?? (category === 'social' ? 'link' : CATEGORY_PRESENTATIONS[category].icon),
  }
}
